import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Subscription, catchError, exhaustMap, finalize, from, map, takeWhile, timer } from 'rxjs';
import { DataTaskService } from './data-task.service';
import { AbortAiPromptParams, AbortAiPromptResponse, AiChatItem, AiChatMessageItem, AiChatPanelItem, AiModelIntelligenceItem, AiModelItem, AiToolCallItem, GetAiChatMessagesParams, GetAiChatPanelParams, GetAiChatsParams, GetAiModelIntelligence, GetAiModelIntelligenceParams, GetAiModelsParams, GetAiToolCallsParams, PollAiPromptStatusParams, PollAiPromptStatusResponse, QueueAiPromptParams, QueueAiPromptResponse, SendAiPromptParams } from './ai-chat.types';
import { decodeAiChatBase64, getAiChatPollErrorMessage, getAiChatProviderNames, mapAiChatMessage, sortAiChatsByModifiedDate } from './ai-chat.utils';

export type { AiChatItem, AiChatMessageItem, AiChatMessageResponse, AiChatPanelItem, AiChatPromptContent, AiModelIntelligenceItem, AiModelItem, AiToolCallItem, GetAiChatMessagesParams, GetAiChatPanelParams, GetAiChatsParams, GetAiModelIntelligence, GetAiModelIntelligenceParams, GetAiModelsParams, GetAiToolCallsParams, PollAiPromptStatusParams, PollAiPromptStatusResponse, QueueAiPromptParams, QueueAiPromptResponse, SendAiPromptParams } from './ai-chat.types';

@Injectable({
    providedIn: 'root',
})
export class AiChatService {
    private readonly pollIntervalMs = 1000;
    private readonly pollSubscriptions = new Map<string, Subscription>();

    /** Loaded conversations, ordered by their latest modification date. */
    public readonly chats$ = new BehaviorSubject<AiChatItem[]>([]);

    /** Models available to the current portal user. */
    public readonly models$ = new BehaviorSubject<AiModelItem[]>([]);

    /** Decoded messages and transient polling state, indexed by chat key. */
    public readonly messagesByChat$ = new BehaviorSubject<Record<string, AiChatMessageItem[]>>({});

    /** Provider display names derived from the loaded model rows. */
    public readonly providerNamesByKeyno$ = this.models$.pipe(map(getAiChatProviderNames));

    constructor(private datatask: DataTaskService) {}

    /** Queues a prompt, adds an optimistic message, and starts background polling. */
    public async sendPrompt(params: SendAiPromptParams): Promise<QueueAiPromptResponse> {
        const model = this.models$.value.find((item) => item.ai_model_keyno === `${params.ai_model_keyno}`);

        if (!model) throw new Error('The selected AI model is not available.');

        const queued = await this.queueAiPrompt({
            ai_chat_keyno: params.ai_chat_keyno,
            prompt: params.prompt,
            prompt_textcontent: params.prompt_textcontent,
            prompt_image_urls: params.prompt_image_urls,
            ai_provider_keyno: model.ai_provider_keyno,
            ai_model_keyno: model.ai_model_keyno,
            intelligence_level: params.intelligence_level ?? '',
        });

        if (`${queued.errorcode}` !== '0') throw new Error(queued.errormessage || 'The message could not be queued.');

        this.upsertMessage(this.createQueuedMessage(params, queued));
        this.startPolling(queued.ai_message_keyno);

        return queued;
    }

    /** Requests cancellation; polling delivers the final response or cancellation error. */
    public async abortPrompt(params: AbortAiPromptParams): Promise<AbortAiPromptResponse> {
        return (await this.datatask.Post(4063, params))[0];
    }

    /**
     * Queues a prompt and starts one background polling stream for its message.
     *
     * @datatask 4050
     * @datataskName tt_ai_queue_prompt
     * @returns The queued message and chat identifiers.
     */
    public async queueAiPrompt(params: QueueAiPromptParams): Promise<QueueAiPromptResponse> {
        return (await this.datatask.Post(4050, params))[0];
    }

    /**
     * Retrieves the current processing state for one queued message.
     *
     * @datatask 4051
     * @datataskName tt_ai_poll_response_status
     * @returns The latest provider response, reasoning summary, or error state.
     */
    public async pollAiPromptStatus(params: PollAiPromptStatusParams): Promise<PollAiPromptStatusResponse> {
        return (await this.datatask.Post(4051, params))[0];
    }

    /**
     * @datatask 4047
     * @datataskName tt_ai_chat_list
     * @returns Chats ordered by most recently modified first.
     */
    public async getChats(params?: GetAiChatsParams): Promise<AiChatItem[]> {
        const chats: AiChatItem[] = await this.datatask.Post(4047, params);
        const sortedChats = sortAiChatsByModifiedDate(chats);
        this.chats$.next(sortedChats);
        return sortedChats;
    }

    /**
     * @datatask 4048
     * @datataskName tt_ai_message_list
     * @returns Decoded messages for the requested chat and updates the shared message cache.
     */
    public async getChatMessages(params: GetAiChatMessagesParams): Promise<AiChatMessageItem[]> {
        const messages = await this.getChatMessagePage(params);
        const chatKeyno = params.ai_chat_keyno;

        if (chatKeyno !== undefined) this.setChatMessages(`${chatKeyno}`, messages);

        return chatKeyno === undefined ? messages : (this.messagesByChat$.value[`${chatKeyno}`] ?? messages);
    }

    /**
     * @datatask 4048
     * @datataskName tt_ai_message_list
     * @returns One decoded cursor page without changing the shared polling-message cache.
     */
    public async getChatMessagePage(params: GetAiChatMessagesParams): Promise<AiChatMessageItem[]> {
        return (await this.datatask.Post(4048, params)).map(mapAiChatMessage);
    }

    /**
     * @datatask 4071
     * @returns Tool calls made while resolving messages in the requested chat.
     */
    public async getAiToolCalls(params: GetAiToolCallsParams): Promise<AiToolCallItem[]> {
        return this.datatask.Post(4071, params);
    }

    /**
     * @datatask 4041
     * @datataskName tt_ai_model_list
     * @returns Models available to the current portal user and updates the shared model cache.
     */
    public async getAiModels(params?: GetAiModelsParams): Promise<AiModelItem[]> {
        const models: AiModelItem[] = await this.datatask.Post(4041, params);
        this.models$.next(models);
        return models;
    }

    /**
     * @datatask 4049
     * @returns Intelligence levels supported by the selected model.
     */
    public async getAiModelIntelligence(params: GetAiModelIntelligenceParams): Promise<AiModelIntelligenceItem[]> {
        return this.datatask.Post(4049, params);
    }

    /**
     * @datatask 4052
     * @datataskName tt_ai_chat_panel_get
     * @returns Access, remembered panel state, selected chat, and the default model for a new chat.
     */
    public async getAiChatPanel(params?: GetAiChatPanelParams): Promise<AiChatPanelItem> {
        return (await this.datatask.Post(4052, params))[0];
    }

    private createQueuedMessage(params: SendAiPromptParams, queued: QueueAiPromptResponse): AiChatMessageItem {
        const datetime = new Date().toISOString();

        return {
            ai_message_keyno: queued.ai_message_keyno,
            ai_chat_keyno: queued.ai_chat_keyno,
            message_id: '',
            prompt: params.prompt,
            response: '',
            response_base64: '',
            ai_model_keyno: `${params.ai_model_keyno}` as `${number}`,
            intelligence_level: params.intelligence_level ?? '',
            ai_status_keyno: '0',
            error_message: '',
            start_datetime: datetime,
            end_datetime: '',
            reg_datetime: datetime,
            reasoning_status: '',
            polling: true,
            portal_user_keyno: '0',
        };
    }

    private startPolling(aiMessageKeyno: `${number}`) {
        if (this.pollSubscriptions.has(aiMessageKeyno)) return;

        const subscription = timer(this.pollIntervalMs, this.pollIntervalMs)
            .pipe(
                exhaustMap(() => from(this.pollAiPromptStatus({ ai_message_keyno: aiMessageKeyno }))),
                map((status) => this.applyPollStatus(status)),
                takeWhile((status) => status.poll_finished !== '1', true),
                catchError((error) => {
                    this.applyPollError(aiMessageKeyno, error);
                    return EMPTY;
                }),
                finalize(() => this.pollSubscriptions.delete(aiMessageKeyno)),
            )
            .subscribe();

        this.pollSubscriptions.set(aiMessageKeyno, subscription);
    }

    private applyPollStatus(status: PollAiPromptStatusResponse) {
        const message = this.findMessage(status.ai_message_keyno);

        if (!message) return status;

        const finished = status.poll_finished === '1';
        const responseReady = status.response_ready === '1';

        this.upsertMessage({
            ...message,
            ai_chat_keyno: status.ai_chat_keyno,
            message_id: status.message_id || message.message_id,
            ai_status_keyno: status.ai_status_keyno,
            response_base64: responseReady ? status.response_base64 : message.response_base64,
            response: responseReady ? decodeAiChatBase64(status.response_base64) : message.response,
            error_message: finished && !responseReady ? decodeAiChatBase64(status.error_message_base64) || 'The AI response could not be completed.' : decodeAiChatBase64(status.error_message_base64),
            end_datetime: finished ? new Date().toISOString() : message.end_datetime,
            reasoning_status: decodeAiChatBase64(status.summary_text_base64) || message.reasoning_status,
            polling: !finished,
        });

        return status;
    }

    private applyPollError(aiMessageKeyno: string, error: unknown) {
        const message = this.findMessage(aiMessageKeyno);

        if (!message) return;

        this.upsertMessage({
            ...message,
            error_message: getAiChatPollErrorMessage(error),
            end_datetime: new Date().toISOString(),
            polling: false,
        });
    }

    private findMessage(aiMessageKeyno: string) {
        return Object.values(this.messagesByChat$.value)
            .flat()
            .find((message) => message.ai_message_keyno === aiMessageKeyno);
    }

    private setChatMessages(chatKeyno: string, messages: AiChatMessageItem[]) {
        const currentMessages = this.messagesByChat$.value[chatKeyno] ?? [];
        const loadedMessageKeys = new Set(messages.map((message) => message.ai_message_keyno));
        const mergedMessages = messages.map((message) => {
            const current = currentMessages.find((item) => item.ai_message_keyno === message.ai_message_keyno);
            return current?.polling !== undefined ? { ...message, ...current } : message;
        });

        mergedMessages.push(...currentMessages.filter((message) => !loadedMessageKeys.has(message.ai_message_keyno)));
        this.messagesByChat$.next({ ...this.messagesByChat$.value, [chatKeyno]: mergedMessages });
    }

    private upsertMessage(message: AiChatMessageItem) {
        const chatKeyno = message.ai_chat_keyno;
        const messages = this.messagesByChat$.value[chatKeyno] ?? [];
        const existingIndex = messages.findIndex((item) => item.ai_message_keyno === message.ai_message_keyno);
        const updatedMessages = existingIndex === -1 ? [...messages, message] : messages.map((item, index) => (index === existingIndex ? message : item));

        this.messagesByChat$.next({ ...this.messagesByChat$.value, [chatKeyno]: updatedMessages });
    }
}
